<template>
    <div class="w-full h-full">
        <transition
            enter-active-class="transition duration-400 ease-in"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition duration-100 ease-out"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
            class="h-full"
        >
            <div v-if="isLoading" class="fixed top-0 bottom-0 left-0 right-0 bg-black/[0.7] flex flex-col justify-center items-center z-40"
                    tabindex="-1" >
                <div class="flex flex-col justify-center items-center p-4 rounded-md">
                <svg aria-hidden="true" class="mx-1 w-8 h-8 text-gray-200 animate-spin dark:text-gray-600"
                    viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                            fill="#d3d3d3"/>
                        <path
                            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                            fill="#2E9CCA"/>
                </svg>
                <div class="text-white p-4 whitespace-nowrap text-sm">
                    Please Wait While We Make Changes To {{ client.client_name }}.
                </div>
                </div>
            </div>
        </transition>
        <div class="pt-1 w-full mb-2">
            <BaseTabBar 
                :tabList="tabList"
                :selectedColor="selectedColor"
                :notSelectedColor="notSelectedColor"
                :hoverColor="hoverColor"
                @set-page="setView"
            />
        </div>
        <div v-show="isClientFormVisible" class="px-2 py-1 overflow-y-scroll h-[95%]">
            <ClientForm
                :client="client"
                :index="index"
                :account_id="account_id"
                :add-or-edit="addOrEdit"
                @submit-edit-form="submitEditForm"
                @close-edit-form="closeEditModal"
                @refetch-clients="fetchNewClients"
                class="mt-3"

            />
        </div>
        <div v-if="isWorkflowVisible" class="px-2 py-1 overflow-y-scroll h-[95%]">
            <ClientWorkFlow
                :client="client"
                :workflow-steps="workflowSteps"
                @refetch-clients="fetchNewClients"
                class="mt-3"

            />
        </div>
        <div v-if="isTasksVisible" class="px-2 py-1 overflow-y-scroll h-[95%]">
            <ClientTasksPage 
            :client="client"
            :client-task-types="clientTaskTypes"
            :user-options="userOptions"
            @refetch-clients="fetchNewClients" />
        </div>
        <div v-if="isTransactionsVisible" class="px-2 py-1 overflow-y-scroll h-[95%]">
            <ClientTransactions 
            :client="client"
            @refetch-clients="fetchNewClients" />
        </div>
        <div v-if="isPostsVisible" class="px-2 py-1 overflow-y-scroll h-[95%]">
            <AllClientPosts
            :client="client"
            :user-options="userOptions"
            @refetch-clients="fetchNewClients" />
        </div>
        <div v-if="isEngagementsVisible" class="px-2 py-1 overflow-y-scroll h-[95%]">
            <ClientEngagements :client="client" />
        </div>
    </div>
</template>

<script setup>
import { ref, defineProps, defineEmits, computed } from 'vue'
import BaseTabBar from '../BaseTabBar.vue';
import ClientForm from './ClientForm.vue';
import ClientWorkFlow from './ClientWorkflow.vue'
import ClientTasksPage from '../client-tasks/ClientTasksPage.vue';
import ClientTransactions from './ClientTransactions.vue';
import AllClientPosts from './AllClientPosts.vue';
import ClientEngagements from './ClientEngagements.vue';
import { getUsers } from '@/services/User';
import { useStore } from 'vuex';

const props = defineProps([
    "client", "index", "account_id", "addOrEdit", "workflowSteps", "clientTaskTypes", "defaultTab"
])

const store = useStore()

const emit = defineEmits(["submit-edit-form","close-edit-form", "submit-add-form", "close-add-form", "refetch-clients"]);

function fetchNewClients(){
    emit('refetch-clients')
}

function submitEditForm(id, formData, closeAfterSave){
    emit('submit-edit-form', id, formData, closeAfterSave)
}

function closeEditModal(){
    emit('close-edit-form')
}

  
  const userOptions = ref([
      {value: null, label: 'None'}
  ])

const canViewWorkflows = computed(() => {
    let userPermissionsStore = store.getters['auth/showUserPermissions']
    return userPermissionsStore.isViewClientWorkflow || userPermissionsStore.isDev
})

const canViewTransactions = computed(() => {
    let userPermissionsStore = store.getters['auth/showUserPermissions']
    return userPermissionsStore.isViewClientTransactions || userPermissionsStore.isDev
})

const isLoading = ref(false)

const tabList = computed(() => {
    const tabs = [{tabName: 'Edit Client', isSelected: true}]
    if(canViewWorkflows.value){
        tabs.push({tabName: 'Workflow', isSelected: false} )
    }
    if(canViewTransactions.value){
        tabs.push({tabName: 'Transactions', isSelected: false} )
    }
    tabs.push({tabName: "Client Tasks", isSelected: false})
    tabs.push({tabName: "Engagements", isSelected: false})
    tabs.push({tabName: "Posts", isSelected: false})
    return tabs
}
)
const selectedColor = 'white';
const notSelectedColor = 'gray-100'
const hoverColor = 'slate-50'

const isClientFormVisible = ref(true)
const isWorkflowVisible = ref(false)
const isTasksVisible = ref(false)
const isTransactionsVisible = ref(false)
const isPostsVisible = ref(false)
const isEngagementsVisible = ref(false)


function setDefaultTab(){
    if(props.defaultTab){
        setView(props.defaultTab)
    }
}

setDefaultTab()

function showEditClient(){
    isWorkflowVisible.value = false;
    isTasksVisible.value = false;
    isTransactionsVisible.value = false
    isPostsVisible.value = false
    isClientFormVisible.value = true
    tabList.value.forEach((tab) => {
        if(tab.tabName === "Edit Client"){
            tab.isSelected = true
        } else {
            tab.isSelected = false
        }
    })
}

function showWorkflow(){
    isClientFormVisible.value = false
    isTasksVisible.value = false
    isTransactionsVisible.value = false
    isPostsVisible.value = false
    isWorkflowVisible.value = true;
    tabList.value.forEach((tab) => {
        if(tab.tabName === "Workflow"){
            tab.isSelected = true
        } else {
            tab.isSelected = false
        }
    })
}

function showTasks(){
    isClientFormVisible.value = false
    isWorkflowVisible.value = false;
    isTransactionsVisible.value = false
    isPostsVisible.value = false
    isTasksVisible.value = true;
    tabList.value.forEach((tab) => {
        if(tab.tabName === "Client Tasks"){
            tab.isSelected = true
        } else {
            tab.isSelected = false
        }
    })
}

function showTransactions(){
    isClientFormVisible.value = false
    isWorkflowVisible.value = false;
    isTasksVisible.value = false;
    isPostsVisible.value = false;
    isTransactionsVisible.value = true
    tabList.value.forEach((tab) => {
        if(tab.tabName === "Transactions"){
            tab.isSelected = true
        } else {
            tab.isSelected = false
        }
    })
}

function showPosts(){
    isClientFormVisible.value = false
    isWorkflowVisible.value = false;
    isTasksVisible.value = false;
    isTransactionsVisible.value = false
    isPostsVisible.value = true;
    tabList.value.forEach((tab) => {
        if(tab.tabName === "Posts"){
            tab.isSelected = true
        } else {
            tab.isSelected = false
        }
    })
}

function showEngagements(){
    isClientFormVisible.value = false
    isWorkflowVisible.value = false;
    isTasksVisible.value = false;
    isTransactionsVisible.value = false;
    isPostsVisible.value = false;
    isEngagementsVisible.value = true;
    tabList.value.forEach((tab) => {
        tab.isSelected = tab.tabName === "Engagements"
    })
}

function setView(tabName) {
    isEngagementsVisible.value = false;
    if (tabName == 'Edit Client') {
        showEditClient();
    } else if (tabName == 'Workflow') {
        showWorkflow();
    } else if (tabName == 'Client Tasks'){
        showTasks()
    } else if (tabName == 'Transactions'){
        showTransactions()
    } else if (tabName == 'Posts'){
        showPosts()
    } else if (tabName == 'Engagements'){
        showEngagements()
    }
}

  async function setUserOptions() {
      let payload = {
          token: store.state.auth.token,
          endpoint: '?group_id=1&group_id=7&group_id=8&exclude_dev=True&is_active=True'
      }
      const result = await getUsers(payload)
      userOptions.value = [{value: null, label: 'None'}, ...result.users.map((user) => {
          return { value: user.id, label: `${user.first_name} ${user.last_name}`}
      })]
  }

  setUserOptions()


</script>